// src/pages/AddStudent.js
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { addStudentToCourse, getMyCourses } from '../../../api/teacherApi';
import './AddStudent.css';

function AddStudent() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preselectedCourse = queryParams.get('course');

  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    courseId: preselectedCourse || '',
    studentEmail: ''
  });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [recentlyAdded, setRecentlyAdded] = useState([]);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setPageLoading(true);
      const data = await getMyCourses();
      setCourses(data);
      
      // If preselectedCourse exists and is valid, select it
      if (preselectedCourse && data.some(c => c._id === preselectedCourse)) {
        setFormData(prev => ({ ...prev, courseId: preselectedCourse }));
      } else if (data.length > 0 && !formData.courseId) {
        setFormData(prev => ({ ...prev, courseId: data[0]._id }));
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Error loading courses: ' + (error.response?.data?.msg || error.message) 
      });
    } finally {
      setPageLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await addStudentToCourse(formData.courseId, formData.studentEmail);
      
      setMessage({ 
        type: 'success', 
        text: result.msg || 'Student added successfully!' 
      });
      
      // Add to recently added list
      const course = courses.find(c => c._id === formData.courseId);
      setRecentlyAdded(prev => [
        {
          email: formData.studentEmail,
          course: course?.title,
          timestamp: new Date().toLocaleString('tr-TR')
        },
        ...prev.slice(0, 4) // Keep last 5 records
      ]);

      setFormData(prev => ({ ...prev, studentEmail: '' }));
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.msg || error.response?.data?.message || 'Error adding student.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getSelectedCourse = () => {
    return courses.find(course => course._id === formData.courseId);
  };

  if (pageLoading) {
    return (
      <div className="add-student-loading">
        <div className="loading-spinner">👨‍🎓</div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="add-student">
      <div className="form-header">
        <h1>👨‍🎓 Add Student</h1>
        <p>Use the form below to add new students to your course</p>
      </div>

      <div className="content-grid">
        {/* Form Section */}
        <div className="form-section">
          <form onSubmit={handleSubmit} className="student-form">
            <div className="form-group">
              <label htmlFor="courseId">Select Course *</label>
              {courses.length === 0 ? (
                <div className="no-courses-warning">
                  <p>You don't have any courses yet. You need to create a course first.</p>
                  <button 
                    type="button"
                    onClick={() => navigate('/teacher/create-course')}
                    className="create-course-btn"
                  >
                    Create Course
                  </button>
                </div>
              ) : (
                <select
                  id="courseId"
                  name="courseId"
                  value={formData.courseId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a course</option>
                  {courses.map(course => (
                    <option key={course._id} value={course._id}>
                      {course.title} ({course.students?.length || 0} students)
                    </option>
                  ))}
                </select>
              )}
            </div>

            {formData.courseId && (
              <>
                <div className="form-group">
                  <label htmlFor="studentEmail">Student Email *</label>
                  <input
                    type="email"
                    id="studentEmail"
                    name="studentEmail"
                    value={formData.studentEmail}
                    onChange={handleChange}
                    required
                    placeholder="example@email.com"
                  />
                </div>

                {getSelectedCourse() && (
                  <div className="course-info">
                    <h4>Selected Course Information</h4>
                    <p><strong>Course:</strong> {getSelectedCourse()?.title}</p>
                    <p><strong>Current Students:</strong> {getSelectedCourse()?.students?.length || 0}</p>
                    <p><strong>Category:</strong> {getSelectedCourse()?.category || 'Not specified'}</p>
                  </div>
                )}
              </>
            )}

            {message.text && (
              <div className={`message ${message.type}`}>
                {message.text}
              </div>
            )}

            {formData.courseId && (
              <div className="form-actions">
                <button 
                  type="submit" 
                  disabled={loading}
                  className="submit-btn"
                >
                  {loading ? '⏳ Adding...' : '✅ Add Student'}
                </button>
                <button 
                  type="button"
                  onClick={() => navigate('/teacher/my-courses')}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Recent Activity Section */}
        <div className="info-section">
          <div className="info-card">
            <h3>📋 Recently Added</h3>
            {recentlyAdded.length === 0 ? (
              <p className="no-data">No students added yet</p>
            ) : (
              <div className="recent-list">
                {recentlyAdded.map((item, index) => (
                  <div key={index} className="recent-item">
                    <div className="recent-email">{item.email}</div>
                    <div className="recent-course">{item.course}</div>
                    <div className="recent-time">{item.timestamp}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="info-card">
            <h3>💡 Tips</h3>
            <ul className="tips-list">
              <li>Student must be registered in the system</li>
              <li>Make sure the email address is correct</li>
              <li>You'll get an error if the student is already enrolled</li>
              <li>If student doesn't exist, they will be created automatically</li>
              <li>A temporary password will be sent via email</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddStudent;